import { Graph, type FramePixels } from "@/libs/cv"
import {
  create as createFaceMeshGraph,
  type FaceDetection,
} from "@/libs/cv/graphs/face-mesh/face-mesh"
import {
  create as createIlluminationGraph,
  type IlluminationDetection,
} from "@/libs/cv/graphs/illumination/illumination"
import { create as createQualityGraph, type QualityMetrics } from "@/libs/cv/graphs/quality/quality"
import { createDummyAsyncIterator } from "@/libs/cv/graphs/utils"
import type { Features } from "@/libs/feature-toggle"
import type { ImageSource } from "@/libs/image"
import { mapValues } from "@/libs/lodash"
import { mean, round } from "@/libs/math"
import type { PerformanceEntry } from "@/libs/telemetry/performance"
import { getLightingIntensityThresholds } from "./face/validation"

export { defaultIllumination } from "@/libs/cv/modules/illumination/illumination"

export interface Detections {
  face: FaceDetection | null
  illumination: IlluminationDetection
  quality: QualityMetrics
}

type Progress = { complete: number }

export const defaultQuality: QualityMetrics = {
  blurScore: 61.1575,
  noiseScore: 4,
  resolutionScore: 100,
  fullFaceVisible: true,
}

export type CreateOptions = {
  performance?: PerformanceEntry
  onProgress?: (progress: Progress) => void
  features?: Features
  initialState?: { illumination: { intensity: number } }
  /** When true, the preset will assume the input is a static image upload and use custom conversion */
  staticImage?: boolean
}

export const create = async (opts?: CreateOptions) => {
  const perf = opts?.performance?.mark("cv:preset-creation")

  // 1 for face mesh, treated as 40% of total
  // 1 for illumination, treated as  40% of total
  // 1 for extra non-network tasks like models compilation, treated as  20% of total
  const progresses: number[] = [0, 0, 0]
  const onProgress = opts?.onProgress
  const notify = onProgress
    ? (index: number, { complete }: Progress) => {
        progresses[index] = complete
        const aggregated = progresses.reduce((acc, c, idx) => acc + c * (idx < 2 ? 0.4 : 0.2), 0)
        onProgress({ complete: aggregated })
      }
    : undefined

  const [faceMeshGraph, illuminationGraph, qualityGraph] = await Promise.all([
    createFaceMeshGraph({
      performance: perf,
      onProgress: notify && notify.bind(null, 0),
      features: opts?.features,
    }),
    createIlluminationGraph({
      performance: perf,
      onProgress: notify && notify.bind(null, 1),
      initialState: {
        // Assume illumination is perfect at the beginning.
        intensity:
          opts?.initialState?.illumination.intensity ??
          mean(getLightingIntensityThresholds().perfect),
      },
    }),
    opts?.staticImage ? createQualityGraph() : Promise.resolve(null),
  ])

  if (notify) notify(2, { complete: 1 })

  perf?.measure({
    detail:
      typeof navigator.connection === "undefined"
        ? undefined
        : {
            "connection.type": navigator.connection.type,
            "connection.effectiveType": navigator.connection.effectiveType,
            "connection.downlink": navigator.connection.downlink,
            "connection.saveData": navigator.connection.saveData,
          },
  })

  return new Preset(faceMeshGraph, illuminationGraph, qualityGraph)
}

export type { Preset, PresetThrottleParams }

type PresetThrottleParams = Record<"face" | "illumination", number | null>

class Preset {
  private readonly _face: AsyncIterator<FaceDetection | null, FaceDetection | null>
  private readonly _illumination: AsyncIterator<IlluminationDetection, IlluminationDetection>
  private readonly _quality: AsyncIterator<QualityMetrics, QualityMetrics, QualityMetrics>

  constructor(
    private readonly _faceGraph: Graph<[ImageSource], [FaceDetection | null]>,
    private readonly _illuminationGraph: Graph<
      [FaceDetection, FramePixels],
      [IlluminationDetection]
    >,
    private readonly _qualityGraph: Graph<
      [ImageSource, FaceDetection | null],
      [QualityMetrics]
    > | null,
  ) {
    this._face = _faceGraph.out(0).data()
    this._illumination = _illuminationGraph.out(0).data()

    // If qualityGraph exists, use its iterator; otherwise, create a dummy iterator.
    this._quality = _qualityGraph?.out(0).data() ?? createDummyAsyncIterator(defaultQuality)
  }

  async getDetections(frame: ImageSource): Promise<FaceDetection | null> {
    this._faceGraph.in(0).write(frame)
    const result = await this._face.next()

    if (this._faceGraph.hasError())
      throw new AggregateError(this._faceGraph.getComposedErrors(), "Face processing failed")

    return result.value
  }
  async getIlluminationEstimation(
    detection: FaceDetection,
    pixels: FramePixels,
  ): Promise<IlluminationDetection> {
    this._illuminationGraph.in(0).write(detection)
    this._illuminationGraph.in(1).write(pixels)

    const result = await this._illumination.next()

    if (this._illuminationGraph.hasError())
      throw new AggregateError(
        this._illuminationGraph.getComposedErrors(),
        "Illumination processing failed",
      )

    return mapValues(result.value, (value) => round(value, 2))
  }
  async getQualityEstimation(
    frame: ImageSource,
    detection: FaceDetection | null,
  ): Promise<QualityMetrics> {
    this._qualityGraph?.in(0).write(frame)
    this._qualityGraph?.in(1).write(detection)

    const result = await this._quality.next()

    if (this._qualityGraph?.hasError())
      throw new AggregateError(this._qualityGraph.getComposedErrors(), "Quality processing failed")

    return result.value
  }
}

declare global {
  interface Navigator {
    // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/connection
    connection?: {
      type: string
      effectiveType: string
      downlink: number
      saveData: boolean
    }
  }
}
